import * as PIXI from 'pixi.js';
import { TextBoxHeightState } from '../../enums';
import { Config } from '../../video-data/config';
import { canMeasureText } from './TextAsset/textMetricsHelpers';
import type { AssetGroup } from './AssetGroup';
import type { TextAsset } from './TextAsset/TextAsset';
import type { Template } from '../template/Template';
import type { BaseAsset, CopyPasteData, CopyPasteStyle } from './BaseAsset';

/**
 * Provided template reference map, find values that are not available in Template.
 */
export const findTemplateIndexErrors = (
    template: Template,
    path: string,
    value: string | object,
    templateIndexErrorsList: string[],
) => {
    if (typeof value === 'object') {
        // Recursively parse object
        Object.entries(value).forEach(([path, value]) =>
            findTemplateIndexErrors(template, path, value as string, templateIndexErrorsList),
        );
        return;
    }

    // Assume value is a string
    const [type, index] = value.split('_');
    const indexVal = parseInt(index);
    const length = (template as unknown as Record<string, { length: number }>)[type].length;
    if (indexVal >= length) {
        templateIndexErrorsList.push(
            `${path} uses ${value}, but only ${length - 1} ${type} available.`,
        );
    }
};

/**
 * Provided an object implementing CopyPasteStyle, gets properties & values
 * which are part of dynamic styling, along with a template reference map
 * of the object.
 */
export const getStylePropertiesToCopy = (obj: CopyPasteStyle): CopyPasteData => {
    const serializedObject = obj.save();
    const settings: Record<string, unknown> = {};
    const propertiesToCopy = obj.stylePropertiesToCopy;

    // We don't necessarily want to copy every property. Hand pick these:
    propertiesToCopy.forEach((propertyName) => {
        settings[propertyName] = serializedObject[propertyName];
    });

    let templateReferenceMap = {};
    templateReferenceMap = obj.buildTemplateReferenceMap();
    settings._templateReferenceMap = templateReferenceMap;

    return settings as CopyPasteData;
};

export const equalWithinEpsilon = (a: number, b: number, epsilon: number = 0.001) => {
    return Math.abs(a - b) < epsilon;
};

export function greaterThanOrEqualWithinEpsilon(a: number, b: number, epsilon = 0.001): boolean {
    return a >= b || equalWithinEpsilon(a, b, epsilon);
}

export function resetSprite(sprite: PIXI.Sprite) {
    if (sprite.destroyed) {
        return;
    }
    const width = sprite.width;
    const height = sprite.height;
    sprite.texture = PIXI.Texture.EMPTY;
    sprite.scale._x = 1;
    sprite.scale._y = 1;
    sprite.width = width;
    sprite.height = height;
}

/**
 * Given a list of names, and a prefix, generate a unique name.
 *
 * For example, ['one', 'two'] and 'two' generates 'two_1'.
 */
export function uniqueName(prefix: string, names: string[]) {
    // Reduce the search space.
    names = names.filter((name) => name.startsWith(prefix));

    let count = 1;

    while (true) {
        const test = `${prefix}_${count}`;
        if (!names.find((name) => name.startsWith(test))) {
            return test;
        }
        count += 1;
    }
}

export function getTextBoxHeightGovernor<T extends BaseAsset>(asset: T): T | AssetGroup {
    const owner = asset.getFreelyPositionedParent();
    return owner !== asset && Config.isAsset(owner) && Config.isAssetGroup(owner) ? owner : asset;
}

/**
 * Whether the box's own height governs what it draws, rather than its text doing so: either the
 * user fixed a height or auto-resizing is suspended while something other than a person writes.
 */
export function isHeightHeld(box: TextAsset | AssetGroup): boolean {
    return box.hasUserChangedMaxHeight || !box.shouldAutoResizeToFitText;
}

export function resolveIsHeightMatchingText(
    box: TextAsset | AssetGroup,
    governor: TextAsset | AssetGroup,
): boolean {
    if (isHeightHeld(box)) {
        return false;
    }
    if (!canMeasureText()) {
        return false;
    }
    return governor === box;
}

export function resolveTextBoxHeightState(
    box: TextAsset | AssetGroup,
    governor: TextAsset | AssetGroup,
): TextBoxHeightState {
    if (governor !== box) {
        return governor.heightState;
    }
    if (!canMeasureText() || !isHeightHeld(box)) {
        return TextBoxHeightState.Normal;
    }
    // A height within `LINE_HEIGHT_RENDER_SLACK` of the text is a resize that landed on it.
    const { maxHeight, unclippedHeight } = box;
    if (maxHeight < unclippedHeight - Config.LINE_HEIGHT_RENDER_SLACK) {
        return TextBoxHeightState.Truncated;
    }
    if (maxHeight > unclippedHeight + Config.LINE_HEIGHT_RENDER_SLACK) {
        return TextBoxHeightState.Enlarged;
    }
    return TextBoxHeightState.Normal;
}
